"use client";

import { useState } from "react";
// framer-motion was previously imported here for accordion animations —
// replaced with the pure-CSS grid-template-rows trick to drop ~40KB gz.
import { Plus, Minus, ArrowRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

interface FAQ {
    question: string;
    answer: string | React.ReactNode;
}

const faqs: FAQ[] = [
    // Research & Intended Use
    {
        question: "Can you provide instructions on how to use or reconstitute these peptides?",
        answer: "No. All products sold on this site are strictly for in vitro laboratory research use only and are not for human consumption. Under regulatory guidelines, we are prohibited from providing any information regarding the administration, use, or handling of these products for humans or animals. Any communication implying intended human use will result in the immediate cancellation of your order and blacklisting from our platform."
    },
    {
        question: "Are your products safe for human use?",
        answer: "These products are classified as research chemicals, not medications. They have not been approved by the FDA for human use and are not intended to diagnose, treat, cure, or prevent any disease. They are sold solely for the purpose of laboratory experimentation and development."
    },
    {
        question: "What is the intended use of your peptides?",
        answer: "Our peptides are intended strictly for in vitro laboratory research and educational purposes. They are used by researchers, academic institutions, and laboratories for studying biological processes, developing assays, and advancing scientific knowledge. These products are not for human or animal consumption."
    },

    // Quality & Testing
    {
        question: "How do I know your peptides are pure?",
        answer: (
            <div className="space-y-4">
                <p>We prioritize radical transparency. Every batch is tested by an independent, third-party laboratory. We display Certificates of Analysis (COAs) for every product, which include:</p>
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong className="text-slate-900">HPLC Purity:</strong> Verifying the chemical purity of the sample (typically ≥99%) to ensure the absence of synthesis byproducts.</li>
                    <li><strong className="text-slate-900">Mass Spectrometry (MS):</strong> Verifying the molecular identity and sequence of the peptide. We use reputable third-party labs (such as Janoshik Analytics) for verification. You can cross-reference the verification key on the COA directly with the testing lab to ensure authenticity.</li>
                </ul>
            </div>
        )
    },
    {
        question: "Why is the \"Purity\" different from the \"Net Peptide Content\"?",
        answer: (
            <div className="space-y-4">
                <p>This is a common misunderstanding in peptide science.</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong className="text-slate-900">Purity (HPLC):</strong> Indicates the percentage of the target peptide relative to other impurities (like synthesis byproducts).</li>
                    <li><strong className="text-slate-900">Net Peptide Content:</strong> Refers to the actual weight of the peptide in the vial versus the total weight, which includes residual water and counter-ions (like salts) bound during the lyophilization process. A vial may be 99% pure but have a net peptide content of ~80%. This is normal for lyophilized peptides. Researchers should account for Net Peptide Content when calculating molar concentrations for in vitro experiments.</li>
                </ul>
            </div>
        )
    },
    {
        question: "Do you test for endotoxins?",
        answer: "Our standard COAs verify identity and chemical purity. Please note that because our products are for Research Use Only and not for human injection, we do not claim pharmaceutical-grade sterility (GMP) unless explicitly stated for specific bulk orders intended for further manufacturing."
    },
    {
        question: "Where are your peptides synthesized?",
        answer: "Our peptides are synthesized by reputable manufacturers with established quality control protocols. Each batch undergoes rigorous third-party testing to verify purity and identity before being made available. We maintain strict supplier qualifications and regularly audit our supply chain to ensure consistent quality."
    },
    {
        question: "What does ≥99% purity mean?",
        answer: "A purity of ≥99% indicates that at least 99% of the sample consists of the target peptide, with less than 1% being synthesis-related impurities such as truncated sequences, deletion sequences, or other byproducts. This is verified through High-Performance Liquid Chromatography (HPLC) analysis and represents research-grade quality suitable for in vitro studies."
    },

    // Shipping & Storage
    {
        question: "My order arrived at room temperature. Is the product ruined?",
        answer: "No. Lyophilized (freeze-dried) peptides are highly stable at room temperature for days or even weeks. The lyophilization process removes water, which prevents degradation during transit. While we may ship with ice packs during extreme heatwaves as a precaution, receiving products at ambient temperature does not compromise their integrity."
    },
    {
        question: "How should I store the peptides once received?",
        answer: (
            <div className="space-y-4">
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong className="text-slate-900">Lyophilized (Powder):</strong> Store in the freezer at -20°C or colder. Keep away from bright light.</li>
                    <li><strong className="text-slate-900">Reconstituted (Liquid):</strong> Once dissolved in a solvent, the peptide becomes much less stable. It should be kept refrigerated (4°C) and used quickly. Avoid repeated freeze-thaw cycles, as this can degrade the peptide structure.</li>
                </ul>
            </div>
        )
    },
    {
        question: "The vial looks empty or the \"puck\" is broken. Is this normal?",
        answer: "Yes. The lyophilization process leaves a small \"cake\" or powder at the bottom of the vial. Sometimes this cake can dislodge or crumble during shipping. This does not affect the chemical integrity or the quantity of the peptide present."
    },
    {
        question: "How long does shipping take?",
        answer: "Orders placed before 2 PM EST on business days are typically dispatched the same day. Standard shipping within the continental United States usually takes 2-5 business days. We provide tracking information via email once your order ships so you can monitor delivery progress."
    },
    {
        question: "Do you ship internationally?",
        answer: "Currently, we only ship within the United States. International shipping involves complex regulatory requirements for research chemicals, and we want to ensure full compliance with all applicable laws. We are continuously evaluating options to expand our shipping capabilities in the future."
    },

    // Orders & Payments
    {
        question: "What payment methods do you accept?",
        answer: "We accept credit cards (Visa, MasterCard), cryptocurrency (Bitcoin, Ethereum, USDC), Venmo, CashApp, and direct bank transfers. Detailed payment instructions are provided during checkout."
    },
    {
        question: "Do you accept returns?",
        answer: "Due to the nature of research chemicals and the risk of contamination once a product leaves our facility, we cannot accept returns. However, if your order arrives damaged, please contact us within 48 hours with photos and we will send a replacement at no additional cost."
    },
    {
        question: "Can I change or cancel my order after placing it?",
        answer: "Yes. Until your order ships, you can manage it yourself from your account. Go to Account, then Orders, and open the order — from there you can update the shipping address, request item or quantity changes, or cancel the order entirely. Cancellations are refunded to your original payment method (card refunds take 3 to 5 business days, and any store credit used returns to your account instantly). Once an order has shipped it can no longer be changed, but our support team is happy to help."
    },
    {
        question: "Can I change my payment method after ordering?",
        answer: "If you placed an order with Venmo, Cash App, or Zelle and haven't sent payment yet, you can switch to a different payment app or pay by card instead. Open the order from your account (Account, then Orders) or from your order confirmation page and choose a new payment option. Unpaid orders are automatically canceled after 48 hours, so switching to a method that works for you keeps your order alive."
    },
    {
        question: "How do I track my order?",
        answer: "Once your order is dispatched, you will receive an email with tracking information. You can also log into your account on our website to view your order history and current order status. If you have any issues with tracking, our support team is happy to assist."
    },

    // Account & General
    {
        question: "Do I need an account to place an order?",
        answer: "Yes, creating an account is required to place orders. This helps us maintain compliance with research chemical regulations, provide order tracking, and offer a better experience with saved addresses and order history. Account creation is free and takes only a minute."
    },
    {
        question: "How do I access my Certificate of Analysis (COA)?",
        answer: "COAs are available on each product page. Simply navigate to the product you purchased and look for the COA tab or document link. You can also access COAs for your specific batch through your order confirmation email or by contacting our support team with your order number."
    },
];

export default function FAQPage() {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [expandedAll, setExpandedAll] = useState(false);

    const toggleExpandAll = () => {
        setExpandedAll(!expandedAll);
        setOpenIndex(null);
    };

    // Generate FAQ JSON-LD schema for SEO
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs
            .filter((faq) => typeof faq.answer === "string")
            .map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer as string,
                },
            })),
    };

    return (
        <div className="bg-slate-50 min-h-screen pb-24 font-sans text-slate-900">
            {/* FAQ Schema Markup */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
            {/* Hero Section */}
            <section className="relative w-full overflow-hidden border-b border-slate-200 bg-slate-100">
                <div className="absolute inset-0">
                    <Image
                        src="/faq-hero.webp"
                        alt=""
                        fill
                        priority
                        sizes="100vw"
                        className="object-cover object-right"
                    />
                    {/* Left-to-right light scrim so the dark headline stays legible */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
                </div>
                <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-28">
                    <div className="mb-4 flex items-center gap-3">
                        <span className="h-px w-8 bg-[#008080]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080]">
                            Resource Center
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                        Frequently Asked Questions
                    </h1>
                    <p className="mt-4 text-lg text-slate-600 max-w-2xl font-medium">
                        Comprehensive answers regarding our laboratory protocols, purity standards, and order fulfillment.
                    </p>
                </div>
            </section>

            {/* Expand/Collapse All */}
            <section className="max-w-3xl mx-auto px-6 pt-12 pb-6">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                        Directory ({faqs.length})
                    </p>
                    <button
                        onClick={toggleExpandAll}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        {expandedAll ? "Collapse All" : "Expand All"}
                    </button>
                </div>
            </section>

            {/* FAQ List */}
            <section className="max-w-3xl mx-auto px-6 pb-16">
                <div className="space-y-4">
                    {faqs.map((faq, index) => {
                        const isOpen = expandedAll || openIndex === index;
                        return (
                            <div
                                key={index}
                                className={`bg-white rounded-xl border transition-all duration-200 ease-in-out ${isOpen
                                        ? "border-blue-200 shadow-md shadow-blue-900/5 ring-1 ring-blue-50"
                                        : "border-slate-200 hover:border-blue-300 hover:shadow-sm"
                                    }`}
                            >
                                <button
                                    onClick={() => {
                                        setExpandedAll(false);
                                        setOpenIndex(openIndex === index ? null : index);
                                    }}
                                    className="w-full py-5 px-6 flex items-start justify-between text-left group focus:outline-none"
                                >
                                    <span className={`text-[1.05rem] font-semibold tracking-tight transition-colors duration-200 pr-8 ${isOpen ? 'text-blue-700' : 'text-slate-800 group-hover:text-blue-700'
                                        }`}>
                                        {faq.question}
                                    </span>
                                    <span className={`mt-0.5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : 'text-slate-400 group-hover:text-blue-500'}`}>
                                        {isOpen ? <Minus size={18} strokeWidth={2.5} /> : <Plus size={18} strokeWidth={2.5} />}
                                    </span>
                                </button>
                                <div
                                    className={`grid transition-all duration-[250ms] ease-out ${
                                        isOpen
                                            ? "grid-rows-[1fr] opacity-100"
                                            : "grid-rows-[0fr] opacity-0"
                                    }`}
                                    aria-hidden={!isOpen}
                                >
                                    <div className="overflow-hidden">
                                        <div className="pb-6 px-6 text-slate-600 leading-relaxed font-medium">
                                            <div className="h-px w-full bg-slate-100 mb-5"></div>
                                            {faq.answer}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Contact Us CTA */}
            <section className="max-w-3xl mx-auto px-6">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 py-12 px-10 rounded-2xl shadow-xl shadow-slate-900/10 relative overflow-hidden border border-slate-700">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -ml-16 -mb-16 pointer-events-none"></div>

                    <div className="relative z-10 flex flex-col items-center text-center space-y-5">
                        <div className="space-y-2">
                            <h3 className="text-2xl font-bold text-white tracking-tight">Need specific technical support?</h3>
                            <p className="text-slate-300 max-w-lg mx-auto font-medium">
                                Our qualified success team stands ready to assist with bulk orders or complex logistics inquiries.
                            </p>
                        </div>
                        <Link prefetch={false}
                            href="/contact"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-bold tracking-wide rounded-lg hover:bg-slate-50 hover:shadow-lg hover:shadow-white/10 transition-all border border-transparent hover:border-slate-200"
                        >
                            Contact Support
                            <ArrowRight size={18} strokeWidth={2.5} className="text-blue-600" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
